import React, { useState, useEffect, useContext, useMemo } from 'react';
import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import axios from 'axios';
import CustomModal from '../../Elements/CustomModal';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import notFoundImage from '../../../assets/fox-not-found.webp';
import { UserContext } from '../../../context/UserContext';
import { getVenueTypeConfig } from '../../../lib/consts/venueTypes';
import { getImageCacheKey, getImageUrl, invalidateImageCache } from '../../../lib/helpers';
import Sentry, { sentryEnabled } from '../../../lib/sentry';
import { clearAllProductsCache } from './useProducts';

function formatApiErrorDetail(detail) {
  if (!detail) return 'Ocurrió un error al guardar el producto';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => formatApiErrorDetail(item))
      .filter(Boolean)
      .join(' ');
  }
  if (typeof detail === 'object') {
    return Object.entries(detail)
      .map(([key, value]) => {
        const formattedValue = Array.isArray(value)
          ? value.join(', ')
          : typeof value === 'object' && value !== null
            ? formatApiErrorDetail(value)
            : String(value);
        return key ? `${key}: ${formattedValue}` : formattedValue;
      })
      .join(' | ');
  }
  return String(detail);
}

function normalizeNumericInput(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** HTML5 `type="number"` solo admite punto decimal; en es-ES el teclado suele meter coma. */
function coerceNumberInputString(value) {
  if (value === '' || value === null || value === undefined) return '';
  return String(value).replace(',', '.');
}

/**
 * Precio sin IVA desde PVP y % IVA: el PVP ya incluye el IVA, así que la base
 * imponible es PVP ÷ (1 + %/100) — no PVP × (1 − %/100), que resta el
 * porcentaje sobre el total y da una base menor de la real.
 * Ej.: 2 € y 10 % → base 1,82 € (IVA 0,18 €).
 */
function computePriceExVatAmount(priceNorm, saleVatNorm) {
  if (priceNorm == null) return null;
  if (saleVatNorm == null) return priceNorm;
  const pct = Math.min(Math.max(saleVatNorm, 0), 100);
  return Number((priceNorm / (1 + pct / 100)).toFixed(2));
}

/** Coste con IVA desde coste sin IVA y % IVA compra: coste × (1 + %/100). */
function computeCostIncVatAmount(costExNorm, purchaseVatNorm) {
  if (costExNorm == null) return null;
  if (purchaseVatNorm == null) return costExNorm;
  const pct = Math.min(Math.max(purchaseVatNorm, 0), 100);
  return Number((costExNorm * (1 + pct / 100)).toFixed(2));
}

/** Valor mostrado en «Precio sin IVA»: lo escrito en el formulario o la base PVP × (1 − %/100) si el campo está vacío. */
function getPriceExVatInputDisplayValue(form) {
  if (form.priceExVat !== '') {
    return coerceNumberInputString(form.priceExVat);
  }
  const saleVat = normalizeNumericInput(form.saleVatRate);
  if (saleVat == null) return '';
  const priceNorm = normalizeNumericInput(form.price);
  const net = computePriceExVatAmount(priceNorm, saleVat);
  if (net == null) return '';
  return coerceNumberInputString(String(net));
}

/** Valor mostrado en «Coste con IVA»: lo escrito en el formulario o coste sin IVA × (1 + % IVA compra) si el campo está vacío. */
function getCostIncVatInputDisplayValue(form) {
  if (form.costIncVat !== '') {
    return coerceNumberInputString(form.costIncVat);
  }
  const cost = normalizeNumericInput(form.costExVat);
  const purchaseVat = normalizeNumericInput(form.purchaseVatRate);
  const gross = computeCostIncVatAmount(cost, purchaseVat);
  if (gross == null) return '';
  return coerceNumberInputString(String(gross));
}

function formatHistoryDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatCurrency(value) {
  if (value === '' || value === null || value === undefined) return '-';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

const UNCATEGORIZED_NAMES = ['Uncategorized', 'uncategorized', 'Sin categoría'];

/** Valor del <select> de categoría al editar (API devuelve category.id, no siempre category_id). */
function resolveProductCategoryFormValue(product) {
  if (!product) return '';
  const rawId = product.category_id ?? product.category?.id;
  if (rawId != null && rawId !== '') {
    return String(rawId);
  }
  const name = product.category?.name;
  if (name && UNCATEGORIZED_NAMES.includes(name)) {
    return '__none__';
  }
  return '__none__';
}

function ProductModal({
  active,
  handleModal,
  token,
  ownedShops,
  setErrorMessage,
  productId,
  productData,
  onSaved,
  // Solo alta: se llama con el producto recién creado para encadenar con Entrar stock.
  onCreated,
  refreshTrigger,
  onOpenStock,
}) {
  const [,, , venueID, venueName, , , , , , , venueType] = useContext(UserContext);
  const shopId = venueID;
  const shopName = venueName || 'Tienda no encontrada';
  const isEdit = Boolean(productId); // ✅ Fix: declare once at top
  const venueConfig = getVenueTypeConfig(venueType);
  const productLabel = venueConfig.productLabel || 'Productos';
  const productEntityLabel = venueConfig.productEntityLabel || 'producto';

  const [form, setForm] = useState({
    name: '',
    price: '',
    priceSecondLine: '',
    priceThirdLine: '',
    stockQuantity: '',
    tareGrams: '',
    // categoryId:
    // - '' => usar categoría por defecto en backend (comportamiento actual)
    // - '__none__' => quitar categoría (Sin categoría)
    categoryId: '',
    unitType: 'fixed',
    isDispensario: false,
    supplierName: '',
    costExVat: '',
    purchaseVatRate: '',
    costIncVat: '',
    saleVatRate: '',
    priceExVat: '',
    trackProfitability: true,
    prepTimeMinutes: '',
    packagingCostPerUnit: '',
    energyCostPerUnit: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [currentImagePath, setCurrentImagePath] = useState(null);
  const [selectedImagePreviewUrl, setSelectedImagePreviewUrl] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  // Alta sin categoría: se avisa de que se usará la de por defecto y se espera respuesta.
  const [pendienteSinCategoria, setPendienteSinCategoria] = useState(false);
  const [costHistory, setCostHistory] = useState([]);
  const [costHistoryLoading, setCostHistoryLoading] = useState(false);
  const [manualCost, setManualCost] = useState({
    supplier_name: '',
    cost_ex_vat: '',
    purchase_vat_rate: '',
    cost_inc_vat: '',
    effective_from: '',
    notes: '',
  });
  const [isSavingManualCost, setIsSavingManualCost] = useState(false);

  const currentImageUrl = useMemo(() => {
    if (!currentImagePath) return null;
    return getImageUrl(currentImagePath, notFoundImage, {
      cacheKey: getImageCacheKey('product', productId || productData?.id),
    });
  }, [currentImagePath, productData?.id, productId]);

  useEffect(() => {
    if (!imageFile) {
      setSelectedImagePreviewUrl(null);
      return undefined;
    }

    const previewUrl = URL.createObjectURL(imageFile);
    setSelectedImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [imageFile]);

  // Fetch categories when modal opens or when categories change elsewhere
  useEffect(() => {
    if (!active || !token || !shopId) return;
    setCategoriesLoading(true);
    axios
      .get('/v1/categories', {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-venue-id': shopId,
        },
      })
      .then(({ data }) => setCategories(data))
      .catch((error) => {
        console.error('Error fetching categories:', error);
        if (setErrorMessage) {
          setErrorMessage('Error al cargar categorías');
        }
      })
      .finally(() => setCategoriesLoading(false));
  }, [active, token, shopId, setErrorMessage, refreshTrigger]);

  // Reset form when modal closes or when switching between create/edit modes
  useEffect(() => {
    if (!active) {
      // Reset form when modal closes
      setForm({
        name: '',
        price: '',
        priceSecondLine: '',
        priceThirdLine: '',
        stockQuantity: '',
    tareGrams: '',
        categoryId: '',
        unitType: 'fixed',
        isDispensario: false,
        supplierName: '',
        costExVat: '',
        purchaseVatRate: '',
        costIncVat: '',
        saleVatRate: '',
        priceExVat: '',
        trackProfitability: true,
        prepTimeMinutes: '',
        packagingCostPerUnit: '',
        energyCostPerUnit: '',
      });
      setImageFile(null);
      setCurrentImagePath(null);
      setCostHistory([]);
      setManualCost({
        supplier_name: '',
        cost_ex_vat: '',
        purchase_vat_rate: '',
        cost_inc_vat: '',
        effective_from: '',
        notes: '',
      });
      return;
    }
  }, [active]);

  useEffect(() => {
    if (!active || isEdit) return;
    setImageFile(null);
    setCurrentImagePath(null);
    // El IVA venta queda VACÍO por defecto al crear. Si se deja vacío, el backend
    // aplica 21% (todo al 21%); las excepciones (cafés/leche 10%) se ponen con los
    // botones de abajo o a mano.
  }, [active, isEdit]);

  // Botones rápidos de IVA venta (21% / 10%). Fija el valor y recalcula el precio sin IVA.
  const applyVatPreset = (val) => {
    setForm((prev) => {
      const updates = { ...prev, saleVatRate: val };
      const saleVat = normalizeNumericInput(val);
      const priceNorm = normalizeNumericInput(prev.price);
      if (saleVat != null && priceNorm != null) {
        const net = computePriceExVatAmount(priceNorm, saleVat);
        if (net != null) updates.priceExVat = String(net);
      }
      return updates;
    });
  };

  // If editing, load existing product details
  useEffect(() => {
    if (!active || !token || !isEdit || !shopId || !productId) return;

    const hydrateProduct = (product) => {
      if (!product) {
        setCurrentImagePath(null);
        setForm({
          name: '',
          price: '',
        priceSecondLine: '',
        priceThirdLine: '',
          stockQuantity: '',
    tareGrams: '',
          categoryId: '',
          unitType: 'fixed',
          isDispensario: false,
          supplierName: '',
          costExVat: '',
          purchaseVatRate: '',
          costIncVat: '',
          saleVatRate: '',
          priceExVat: '',
          trackProfitability: true,
          prepTimeMinutes: '',
          packagingCostPerUnit: '',
          energyCostPerUnit: '',
        });
        return;
      }

      setImageFile(null);
      setCurrentImagePath(product.image_path || null);

      const rawPrice = product.price;
      const rawPriceEx = product.price_ex_vat;
      let priceExVatField = rawPriceEx ?? '';
      if (rawPriceEx != null && rawPriceEx !== '' && rawPrice != null && rawPrice !== '') {
        const p = Number(rawPrice);
        const px = Number(rawPriceEx);
        if (Number.isFinite(p) && Number.isFinite(px) && Math.abs(p - px) < 0.005) {
          // Alta solo con PVP: el backend suele guardar price_ex_vat = price; en edición mostrar vacío.
          priceExVatField = '';
        }
      }

      const rawCostEx = product.cost_ex_vat;
      const rawPurchVat = product.purchase_vat_rate;
      const rawCostInc = product.cost_inc_vat;
      let costIncVatField = rawCostInc ?? '';
      if (rawCostInc != null && rawCostInc !== '' && rawCostEx != null && rawCostEx !== '') {
        const c = Number(rawCostEx);
        const inc = Number(rawCostInc);
        if (Number.isFinite(c) && Number.isFinite(inc)) {
          const pv =
            rawPurchVat != null && rawPurchVat !== ''
              ? Number(rawPurchVat)
              : null;
          const expected = computeCostIncVatAmount(
            c,
            pv != null && Number.isFinite(pv) ? pv : null,
          );
          if (expected != null && Math.abs(inc - expected) < 0.005) {
            costIncVatField = '';
          }
        }
      }

      setForm({
        name: product.name || '',
        price: product.price ?? '',
        priceSecondLine: product.price_second_line ?? '',
        priceThirdLine: product.price_third_line ?? '',
        stockQuantity: product.stock_quantity ?? '',
        tareGrams: product.tare_grams ?? '',
        categoryId: resolveProductCategoryFormValue(product),
        unitType: product.unit_type || 'fixed',
        isDispensario: Boolean(product.is_dispensario),
        supplierName: product.supplier_name || '',
        costExVat: product.cost_ex_vat ?? '',
        purchaseVatRate: product.purchase_vat_rate ?? '',
        costIncVat: costIncVatField,
        saleVatRate: product.sale_vat_rate ?? '',
        priceExVat: priceExVatField,
        trackProfitability: product.track_profitability !== false,
        prepTimeMinutes: product.prep_time_minutes ?? '',
        packagingCostPerUnit: product.packaging_cost_per_unit ?? '',
        energyCostPerUnit: product.energy_cost_per_unit ?? '',
      });
      setManualCost((prev) => ({
        ...prev,
        supplier_name: product.supplier_name || '',
      }));
    };

    if (productData) {
      hydrateProduct(productData);
      return;
    }

    axios
      .get('/v1/get-venue-products', {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-venue-id': shopId,
        },
      })
      .then(({ data }) => hydrateProduct(data.find((p) => p.id === productId)))
      .catch((error) => {
        console.error('Error loading product:', error);
        if (setErrorMessage) {
          setErrorMessage('Error al cargar el producto');
        }
      });
  }, [active, token, isEdit, productId, shopId, setErrorMessage, productData]);

  useEffect(() => {
    if (!active || !token || !isEdit || !productId) return;

    setCostHistoryLoading(true);
    axios
      .get(`/v1/products/${productId}/cost-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => setCostHistory(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error('Error loading cost history:', error);
      })
      .finally(() => setCostHistoryLoading(false));
  }, [active, token, isEdit, productId]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    let next = type === 'checkbox' ? checked : value;
    if (type === 'number' && typeof next === 'string') {
      next = next.replace(',', '.');
    }
    if (name === 'price') {
      setForm((prev) => {
        const updates = { ...prev, price: next };
        const saleVat = normalizeNumericInput(prev.saleVatRate);
        const priceNorm = normalizeNumericInput(next);
        if (saleVat != null && priceNorm != null) {
          const net = computePriceExVatAmount(priceNorm, saleVat);
          if (net != null) updates.priceExVat = String(net);
        } else if (saleVat != null && priceNorm == null) {
          updates.priceExVat = '';
        }
        return updates;
      });
      return;
    }
    if (name === 'saleVatRate') {
      setForm((prev) => {
        const updates = { ...prev, saleVatRate: next };
        const saleVat = normalizeNumericInput(next);
        const priceNorm = normalizeNumericInput(prev.price);
        if (saleVat != null && priceNorm != null) {
          const net = computePriceExVatAmount(priceNorm, saleVat);
          if (net != null) updates.priceExVat = String(net);
        } else {
          updates.priceExVat = '';
        }
        return updates;
      });
      return;
    }
    if (name === 'costExVat') {
      setForm((prev) => {
        const updates = { ...prev, costExVat: next };
        const purchaseVat = normalizeNumericInput(prev.purchaseVatRate);
        const costNorm = normalizeNumericInput(next);
        if (purchaseVat != null && costNorm != null) {
          const gross = computeCostIncVatAmount(costNorm, purchaseVat);
          if (gross != null) updates.costIncVat = String(gross);
        } else if (purchaseVat != null && costNorm == null) {
          updates.costIncVat = '';
        }
        return updates;
      });
      return;
    }
    if (name === 'purchaseVatRate') {
      setForm((prev) => {
        const updates = { ...prev, purchaseVatRate: next };
        const purchaseVat = normalizeNumericInput(next);
        const costNorm = normalizeNumericInput(prev.costExVat);
        if (purchaseVat != null && costNorm != null) {
          const gross = computeCostIncVatAmount(costNorm, purchaseVat);
          if (gross != null) updates.costIncVat = String(gross);
        } else {
          updates.costIncVat = '';
        }
        return updates;
      });
      return;
    }
    setForm((prev) => ({ ...prev, [name]: next }));
  };

  const handleImageChange = e => setImageFile(e.target.files[0]);

  const refreshCostHistory = async () => {
    if (!token || !productId) return;
    setCostHistoryLoading(true);
    try {
      const { data } = await axios.get(`/v1/products/${productId}/cost-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCostHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error refreshing cost history:', error);
    } finally {
      setCostHistoryLoading(false);
    }
  };

  const handleManualCostChange = (e) => {
    const { name, value, type } = e.target;
    let next = value;
    if (type === 'number' && typeof next === 'string') {
      next = next.replace(',', '.');
    }
    setManualCost((prev) => ({ ...prev, [name]: next }));
  };

  // Núcleo compartido del coste manual: registra el POST /costs con lo tecleado
  // (proveedor, fecha efectiva y notas íntegros), sincroniza el form y devuelve
  // la entrada guardada — o null si «Nuevo coste sin IVA» está vacío. Lo usan su
  // botón propio Y el «Actualizar» general del modal: teclear un coste y pulsar
  // el botón grande NO puede descartarlo en silencio (reporte Kong Club 22-08).
  // Proveedor/IVA vacíos caen al valor actual del producto — la sección promete
  // «cambios de coste sin tocar el resto» y el backend sobrescribe con null.
  const flushManualCost = async () => {
    if (!token || !productId) return null;
    const costExVat = normalizeNumericInput(manualCost.cost_ex_vat);
    if (costExVat == null) return null;

    const payload = {
      supplier_name: manualCost.supplier_name || form.supplierName?.trim() || null,
      cost_ex_vat: costExVat,
      purchase_vat_rate:
        normalizeNumericInput(manualCost.purchase_vat_rate) ?? normalizeNumericInput(form.purchaseVatRate),
      cost_inc_vat: normalizeNumericInput(manualCost.cost_inc_vat),
      effective_from: manualCost.effective_from ? new Date(manualCost.effective_from).toISOString() : null,
      notes: manualCost.notes || null,
    };

    const { data } = await axios.post(`/v1/products/${productId}/costs`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    setForm((prev) => ({
        ...prev,
        supplierName: data?.supplier_name || prev.supplierName,
        costExVat: data?.cost_ex_vat ?? prev.costExVat,
        purchaseVatRate: data?.purchase_vat_rate ?? prev.purchaseVatRate,
        costIncVat: data?.cost_inc_vat ?? prev.costIncVat,
      }));
      setManualCost({
        supplier_name: data?.supplier_name || '',
        cost_ex_vat: '',
        purchase_vat_rate: '',
        cost_inc_vat: '',
        effective_from: '',
        notes: '',
      });
    await refreshCostHistory();
    return data;
  };

  const handleSaveManualCost = async () => {
    if (!token || !productId) return;
    if (normalizeNumericInput(manualCost.cost_ex_vat) == null) {
      setLocalError('El nuevo coste sin IVA es obligatorio para guardar un histórico manual.');
      return;
    }
    setIsSavingManualCost(true);
    setLocalError('');
    try {
      const data = await flushManualCost();
      if (onSaved) {
        onSaved(data);
      }
    } catch (error) {
      console.error('Error saving manual cost history:', error);
      setLocalError(error.response?.data?.detail || 'No se pudo guardar el nuevo coste.');
    } finally {
      setIsSavingManualCost(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLocalError('');
    setIsSubmitting(true);

    if (!token || !shopId) {
      setLocalError('Falta autenticación o venue ID');
      setIsSubmitting(false);
      return;
    }

    // Validaciones
    if (!form.name || form.name.trim() === '') {
      setLocalError(`El nombre del ${productEntityLabel} es requerido`);
      setIsSubmitting(false);
      return;
    }

    if (!form.price || parseFloat(form.price) <= 0) {
      setLocalError('El precio debe ser mayor a 0');
      setIsSubmitting(false);
      return;
    }

    // Advertencia si se va a usar la categoría por defecto (solo cuando categoryId === '').
    //
    // Con `window.confirm` esto era el peor de los casos: en la tablet del club el diálogo
    // se traga y devuelve `false` sin enseñar nada, así que rellenabas el alta, pulsabas
    // Crear y NO PASABA NADA — ni producto ni aviso (la caja de errores se acaba de limpiar
    // arriba). El club lo reportó como "da error al crear producto pero no se sabe cuál"
    // (24-08), y habían dado con el disparador solos: pasaba solo sin elegir categoría.
    if (form.categoryId === '') {
      setPendienteSinCategoria(true);
      setIsSubmitting(false);
      return;
    }

    await guardarProducto();
  };

  /** El guardado propiamente dicho, ya validado. Aparte de `handleSubmit` para que el
   *  diálogo de "sin categoría" pueda reanudarlo cuando el usuario dice que sí. */
  const guardarProducto = async () => {
    setLocalError('');
    setIsSubmitting(true);

    // Coste manual tecleado pero sin registrar: «Actualizar» también lo guarda.
    // Antes se descartaba en silencio si no pulsabas «Guardar nuevo coste»
    // (reporte Kong Club 22-08). Se registra PRIMERO (histórico con proveedor/
    // fecha/notas íntegros) y sus valores mandan en el payload del PUT — así el
    // PUT no lo pisa con el coste viejo del closure ni duplica el histórico
    // (el backend solo escribe histórico cuando los campos de coste CAMBIAN).
    let costeManual = null;
    if (isEdit && productId) {
      try {
        costeManual = await flushManualCost();
      } catch (error) {
        console.error('Error saving manual cost history:', error);
        setLocalError(
          (error.response?.data?.detail || 'No se pudo guardar el nuevo coste.') +
            ' El producto no se ha actualizado.'
        );
        setIsSubmitting(false);
        return;
      }
    }

    const formData = new FormData();
    const productPayload = {
      name: form.name.trim(),
      price: parseFloat(form.price),
      // Tara del frasco (peso del bote vacío) — solo tiene sentido en productos por gramo.
      tare_grams: form.unitType === 'weight' && form.tareGrams !== '' ? parseFloat(form.tareGrams) : null,
      // description NO se envía: este formulario no tiene campo para ella, y mandar ''
      // borraba en cada guardado descripciones que sí existen en la BD (p. ej. el
      // THC/CBD migrado de danimateu). Clave ausente = el backend no la toca.
      unit_type: form.unitType,
      venue_id: shopId,
      is_dispensario: Boolean(form.isDispensario),
      supplier_name: costeManual ? (costeManual.supplier_name || null) : (form.supplierName?.trim() || null),
      track_profitability: Boolean(form.trackProfitability),
    };
    const costExVat = normalizeNumericInput(costeManual ? costeManual.cost_ex_vat : form.costExVat);
    const purchaseVatRate = normalizeNumericInput(
      costeManual ? costeManual.purchase_vat_rate : form.purchaseVatRate
    );
    const costIncVat = costeManual
      ? normalizeNumericInput(costeManual.cost_inc_vat)
      : form.costIncVat !== ''
        ? normalizeNumericInput(form.costIncVat)
        : computeCostIncVatAmount(costExVat, purchaseVatRate);
    const saleVatRate = normalizeNumericInput(form.saleVatRate);
    const priceNorm = normalizeNumericInput(form.price);
    const priceExVat =
      form.priceExVat !== ''
        ? normalizeNumericInput(form.priceExVat)
        : computePriceExVatAmount(priceNorm, saleVatRate);
    const prepTimeMinutes = normalizeNumericInput(form.prepTimeMinutes);
    const packagingCostPerUnit = normalizeNumericInput(form.packagingCostPerUnit);
    const energyCostPerUnit = normalizeNumericInput(form.energyCostPerUnit);
    const priceSecondLine = normalizeNumericInput(form.priceSecondLine);
    const priceThirdLine = normalizeNumericInput(form.priceThirdLine);

    // Stock: el alta manda 0 y la edición no manda nada (ver abajo).
    if (!isEdit) {
      // El alta nace SIEMPRE a 0 (Kong, 25-09-2026): el género entra una sola vez, por
      // Entrar stock, con su coste y su ubicación. Sin location_id el backend no siembra
      // nada ni crea filas vacías. El campo sigue en la API para la importación.
      productPayload.stock_quantity = 0;
    }
    // En EDICIÓN no se manda stock jamás (decisión 24-08-2026, incidente Kong Club):
    // el campo viene precargado con el total, así que reenviarlo convertía cualquier
    // guardado de ficha —un precio, una foto— en una corrección de almacén. El backend
    // ya ignora la clave; aquí ni se envía. El stock se corrige en Actualizar stock.

    // Precios por línea de coste (null limpia la línea → cae al precio por defecto)
    productPayload.price_second_line = priceSecondLine;
    productPayload.price_third_line = priceThirdLine;

    if (costExVat != null) productPayload.cost_ex_vat = costExVat;
    if (purchaseVatRate != null) productPayload.purchase_vat_rate = purchaseVatRate;
    if (costIncVat != null) productPayload.cost_inc_vat = costIncVat;
    if (saleVatRate != null) productPayload.sale_vat_rate = saleVatRate;
    if (priceExVat != null) productPayload.price_ex_vat = priceExVat;
    if (prepTimeMinutes != null) productPayload.prep_time_minutes = prepTimeMinutes;
    if (packagingCostPerUnit != null) productPayload.packaging_cost_per_unit = packagingCostPerUnit;
    if (energyCostPerUnit != null) productPayload.energy_cost_per_unit = energyCostPerUnit;
    if (form.categoryId === '__none__') {
      // Quitar categoría explícitamente
      productPayload.category_id = null;
    } else if (form.categoryId) {
      productPayload.category_id = form.categoryId;
    }
    formData.append('product', JSON.stringify(productPayload));
    if (imageFile) formData.append('image', imageFile);

    let guardado = null;
    try {
      const url = isEdit ? `/v1/products/${productId}` : '/v1/products';
      const method = isEdit ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Error al guardar el producto' }));
        const error = new Error(formatApiErrorDetail(errorData.detail));
        error.name = 'ProductSaveError';
        error.status = response.status;
        error.responseBody = errorData;
        error.productPayload = productPayload;
        error.isEdit = isEdit;
        error.productId = productId || null;
        error.shopId = shopId;
        throw error;
      }
      
      const data = await response.json();
      invalidateImageCache(getImageCacheKey('product', data.id || productId));
      clearAllProductsCache();

      // Limpiar formulario
      setForm({
        name: '',
        price: '',
        priceSecondLine: '',
        priceThirdLine: '',
        stockQuantity: '',
    tareGrams: '',
        categoryId: '',
        unitType: 'fixed',
        isDispensario: false,
        supplierName: '',
        costExVat: '',
        purchaseVatRate: '',
        costIncVat: '',
        saleVatRate: '',
        priceExVat: '',
        trackProfitability: true,
        prepTimeMinutes: '',
        packagingCostPerUnit: '',
        energyCostPerUnit: '',
      });
      setImageFile(null);
      guardado = data;
    } catch (err) {
      console.error('Error saving product:', err);
      if (sentryEnabled) {
        Sentry.withScope(scope => {
          scope.setTag('feature_area', 'products');
          scope.setTag('product_modal_mode', isEdit ? 'edit' : 'create');
          scope.setLevel('error');
          scope.setContext('product_modal', {
            is_edit: isEdit,
            product_id: productId || null,
            shop_id: shopId ? String(shopId) : null,
            shop_name: shopName,
            has_image_file: Boolean(imageFile),
            venue_type: venueType || null,
          });
          if (err?.responseBody) {
            scope.setExtra('response_body', err.responseBody);
          }
          if (err?.productPayload) {
            scope.setExtra('product_payload', err.productPayload);
          }
          if (err?.status) {
            scope.setExtra('response_status', err.status);
          }
          Sentry.captureException(err);
        });
      }
      const errorMsg = err.message || 'Ocurrió un error al guardar el producto';
      setLocalError(errorMsg);
      if (setErrorMessage) {
        setErrorMessage(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
    if (!guardado) return;
    // Fuera del try del guardado: el producto YA existe. Un fallo de quien escucha no
    // puede pintarse como «error al guardar» (el reintento lo duplicaría o daría 409).
    try {
      if (onSaved) onSaved(guardado);
      handleModal();
      if (!isEdit && onCreated) onCreated(guardado);
    } catch (err) {
      console.error('Error tras guardar el producto (el producto sí se guardó):', err);
    }
  };

  const modalTitle = isEdit ? `Editar ${productEntityLabel}` : `Crear ${productEntityLabel}`;

  const footer = (
    <>
      <button type="button" className="wa-btn-secondary" onClick={handleModal}>
        Cancelar
      </button>
      <button
        type="submit"
        form="product-modal-form"
        disabled={isSubmitting}
        className="wa-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Guardando...' : (isEdit ? `Actualizar ${productEntityLabel}` : `Crear ${productEntityLabel}`)}
      </button>
    </>
  );

  return (
    <>
    <CustomModal
      active={active}
      handleModal={handleModal}
      title={`${modalTitle} — ${shopName}`}
      size="large"
      titleId="product-modal-title"
      footer={footer}
    >
      <form id="product-modal-form" onSubmit={handleSubmit} className="wa-form space-y-3">
        {localError && (
          <div className="wa-form-error" role="alert">
            {localError}
          </div>
        )}
        <div className="wa-form-grid-cols-2">
          <div className="wa-form-field">
            <label htmlFor="name" className="wa-label">Nombre</label>
            <input
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="wa-input"
            />
          </div>
          <div className="wa-form-field">
            <label htmlFor="price" className="wa-label">Precio</label>
            <input
              id="price"
              name="price"
              type="number"
              step="0.01"
              lang="en"
              value={coerceNumberInputString(form.price)}
              onChange={handleChange}
              required
              className="wa-input"
            />
          </div>
          <div className="wa-form-field">
            <label htmlFor="priceSecondLine" className="wa-label">Precio 2ª línea</label>
            <input
              id="priceSecondLine"
              name="priceSecondLine"
              type="number"
              step="0.01"
              lang="en"
              value={coerceNumberInputString(form.priceSecondLine)}
              onChange={handleChange}
              placeholder="0.00"
              className="wa-input"
            />
            <p className="mt-1 text-xs text-gray-500">Precio para socios con línea de coste «Segunda». Vacío o 0 = se cobra el precio normal.</p>
          </div>
          <div className="wa-form-field">
            <label htmlFor="priceThirdLine" className="wa-label">Precio 3ª línea</label>
            <input
              id="priceThirdLine"
              name="priceThirdLine"
              type="number"
              step="0.01"
              lang="en"
              value={coerceNumberInputString(form.priceThirdLine)}
              onChange={handleChange}
              placeholder="0.00"
              className="wa-input"
            />
            <p className="mt-1 text-xs text-gray-500">Precio para socios con línea de coste «Tercera». Vacío o 0 = se cobra el precio normal.</p>
          </div>
          {isEdit && (
            <div className="wa-form-field">
              <label htmlFor="priceExVat" className="wa-label">Precio sin IVA</label>
              <input
                id="priceExVat"
                name="priceExVat"
                type="number"
                step="0.01"
                lang="en"
                value={getPriceExVatInputDisplayValue(form)}
                onChange={handleChange}
                placeholder="0.00"
                className="wa-input"
              />
              <p className="mt-1 text-xs text-gray-500">Si lo dejas vacío, se rellena con precio final menos el % de IVA venta (también al guardar).</p>
            </div>
          )}
          {/* IVA venta visible también al crear: caja pequeña + presets verdes en línea. Vacío = 21% (backend). */}
          <div className="wa-form-field">
            <label htmlFor="saleVatRate" className="wa-label">IVA venta (%)</label>
            <div className="flex items-center gap-2">
              <input
                id="saleVatRate"
                name="saleVatRate"
                type="number"
                step="0.01"
                lang="en"
                value={coerceNumberInputString(form.saleVatRate)}
                onChange={handleChange}
                placeholder="21"
                className="wa-input"
                style={{ width: '6rem' }}
              />
              <button
                type="button"
                onClick={() => applyVatPreset('21')}
                className="rounded-md px-3 py-1.5 text-sm font-semibold text-[color:var(--wa-brand-foreground)] bg-[color:var(--wa-brand)] hover:bg-[color:var(--wa-brand-hover)] active:opacity-90 transition-colors"
              >
                21%
              </button>
              <button
                type="button"
                onClick={() => applyVatPreset('10')}
                className="rounded-md px-3 py-1.5 text-sm font-semibold text-[color:var(--wa-brand-foreground)] bg-[color:var(--wa-brand)] hover:bg-[color:var(--wa-brand-hover)] active:opacity-90 transition-colors"
              >
                10%
              </button>
            </div>
          </div>
          {isEdit ? (
            <div className="wa-form-field">
              <label htmlFor="stockQuantity" className="wa-label">{venueType === 'bistro' ? 'Stock disponible' : 'Stock'}</label>
              <input
                id="stockQuantity"
                name="stockQuantity"
                type="number"
                lang="en"
                value={coerceNumberInputString(form.stockQuantity)}
                // De solo lectura: el stock vive en las ubicaciones y se corrige contando
                // una de ellas, no escribiendo un total que no corresponde a ningún estante
                // (incidente Kong Club, 22-08-2026).
                disabled
                className="wa-input disabled:bg-[#F5F5F5] disabled:text-gray-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                El stock <strong>no se edita desde aquí</strong>: sale de la suma de tus
                ubicaciones. Para contar una ubicación, entradas con coste o traspasos usa{' '}
                <button
                  type="button"
                  onClick={() => onOpenStock && onOpenStock(productId)}
                  className="font-semibold text-[color:var(--wa-brand)] underline hover:no-underline"
                >
                  Actualizar stock
                </button>.
              </p>
            </div>
          ) : (
            // El alta NO pide stock (Kong, 25-09-2026): había dos puertas para el mismo
            // género —el Stock del alta y Entrar stock— y el club usaba las dos. Ahora hay
            // una: el producto nace a 0 y quien lo abre encadena con Entrar stock (coste,
            // proveedor y ubicación de destino).
            <div className="wa-form-field">
              <p className="text-xs text-[var(--muted-foreground)]">
                {onCreated
                  ? <>El producto se crea con stock 0. Al crearlo se abre <strong>Entrar stock</strong> para meter lo que ha llegado, con su coste y su ubicación.</>
                  : <>El producto se crea con stock 0. Lo que haya llegado se mete desde <strong>Almacén → Actualizar stock → Entrar stock</strong> (hace falta permiso de productos).</>}
              </p>
            </div>
          )}
          <div className="wa-form-field">
            <label htmlFor="categoryId" className="wa-label">Categoría</label>
            <select
              id="categoryId"
              name="categoryId"
              value={form.categoryId}
              onChange={handleChange}
              disabled={categoriesLoading}
              className="wa-select"
            >
              {categoriesLoading ? (
                <option>Cargando…</option>
              ) : (
                <>
                  <option value="__none__">Sin categoría</option>
                  <option value="">Usar categoría por defecto</option>
                  {categories
                    .filter(cat => !UNCATEGORIZED_NAMES.includes(cat.name))
                    .map(cat => (
                      <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
                    ))}
                </>
              )}
            </select>
          </div>
          <div className="wa-form-field">
            <label htmlFor="unitType" className="wa-label">Unidad</label>
            <select
              id="unitType"
              name="unitType"
              value={form.unitType}
              onChange={handleChange}
              required
              className="wa-select"
            >
              <option value="fixed">Fijo (por unidad)</option>
              <option value="weight">Peso (por gramo)</option>
            </select>
          </div>
          {form.unitType === 'weight' && (
            <div className="wa-form-field">
              <label htmlFor="tareGrams" className="wa-label">Tara del frasco (g)</label>
              <input
                id="tareGrams"
                name="tareGrams"
                type="number"
                lang="en"
                step="0.001"
                min="0"
                value={coerceNumberInputString(form.tareGrams)}
                onChange={handleChange}
                className="wa-input"
                placeholder="0.000"
              />
              <p className="mt-1 text-xs text-[#737373]">Peso del bote vacío. Se usa para el ajuste de stock por peso del frasco (neto = bruto − tara).</p>
            </div>
          )}
          <div className="wa-form-field">
            <label htmlFor="supplierName" className="wa-label">Proveedor</label>
            <input
              id="supplierName"
              name="supplierName"
              value={form.supplierName}
              onChange={handleChange}
              placeholder="Proveedor principal"
              className="wa-input"
            />
          </div>
          {isEdit && (
            <>
              <div className="wa-form-field">
                <label htmlFor="costExVat" className="wa-label">Coste sin IVA</label>
                <input
                  id="costExVat"
                  name="costExVat"
                  type="number"
                  step="0.01"
                  lang="en"
                  value={coerceNumberInputString(form.costExVat)}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="wa-input"
                />
              </div>
              <div className="wa-form-field">
                <label htmlFor="purchaseVatRate" className="wa-label">IVA compra (%)</label>
                <input
                  id="purchaseVatRate"
                  name="purchaseVatRate"
                  type="number"
                  step="0.01"
                  lang="en"
                  value={coerceNumberInputString(form.purchaseVatRate)}
                  onChange={handleChange}
                  placeholder="21"
                  className="wa-input"
                />
              </div>
              <div className="wa-form-field">
                <label htmlFor="costIncVat" className="wa-label">Coste con IVA</label>
                <input
                  id="costIncVat"
                  name="costIncVat"
                  type="number"
                  step="0.01"
                  lang="en"
                  value={getCostIncVatInputDisplayValue(form)}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="wa-input"
                />
                <p className="mt-1 text-xs text-gray-500">Si lo dejas vacío, se rellena con coste sin IVA más el % de IVA compra (también al guardar).</p>
              </div>
            </>
          )}
          <div className="wa-form-field wa-form-field-full wa-col-span-2">
            <label htmlFor="image" className="wa-label">
              {isEdit ? `Cambiar imagen del ${productEntityLabel}` : 'Imagen (opcional)'}
            </label>
            <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
              {(currentImageUrl || selectedImagePreviewUrl) ? (
                <div className="flex flex-wrap gap-3">
                  {currentImageUrl && (
                    <div className="flex-shrink-0">
                      <img
                        src={currentImageUrl}
                        alt={`Imagen actual del ${productEntityLabel}`}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = notFoundImage;
                        }}
                        className="h-28 w-28 rounded-xl border border-gray-300 bg-gray-50 object-cover"
                      />
                      <p className="mt-1 text-xs text-gray-500">Actual</p>
                    </div>
                  )}
                  {selectedImagePreviewUrl && (
                    <div className="flex-shrink-0">
                      <img
                        src={selectedImagePreviewUrl}
                        alt={`Nueva imagen del ${productEntityLabel}`}
                        className="h-28 w-28 rounded-xl border border-gray-300 bg-gray-50 object-cover"
                      />
                      <p className="mt-1 text-xs text-gray-500">Nueva</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-28 w-28 flex-shrink-0 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 text-center text-xs text-gray-400">
                  Sin imagen
                </div>
              )}
              <div className="flex-1">
                <input
                  id="image"
                  name="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="wa-file-input block w-full"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {isEdit
                    ? 'Deja el campo vacío para mantener la imagen actual.'
                    : 'Selecciona una imagen JPG, PNG o WEBP si quieres añadirla al crear el producto.'}
                </p>
                {isEdit && selectedImagePreviewUrl && currentImageUrl && (
                  <p className="mt-1 text-xs text-gray-500">
                    La nueva imagen reemplazará la actual al guardar.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {isEdit && (
          <>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Rentabilidad</h3>
                  <p className="text-xs text-gray-500">
                    Activa el seguimiento para incluir este {productEntityLabel} en rentabilidad y menu engineering.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    id="trackProfitability"
                    name="trackProfitability"
                    type="checkbox"
                    checked={form.trackProfitability}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-lime-600 focus:ring-lime-500"
                  />
                  Seguir rentabilidad
                </label>
              </div>
              {!form.costExVat && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Este producto no tiene coste informado. Aparecerá como "sin coste" en rentabilidad.
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Coste operativo unitario</h3>
                <p className="text-xs text-gray-500">
                  Base para margen neto real: tiempo, energia y packaging por unidad.
                </p>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div className="wa-form-field">
                  <label htmlFor="prepTimeMinutes" className="wa-label">Tiempo elaboracion (min)</label>
                  <input
                    id="prepTimeMinutes"
                    name="prepTimeMinutes"
                    type="number"
                    step="0.01"
                    lang="en"
                    value={coerceNumberInputString(form.prepTimeMinutes)}
                    onChange={handleChange}
                    placeholder="0"
                    className="wa-input"
                  />
                </div>
                <div className="wa-form-field">
                  <label htmlFor="energyCostPerUnit" className="wa-label">Energia por unidad</label>
                  <input
                    id="energyCostPerUnit"
                    name="energyCostPerUnit"
                    type="number"
                    step="0.01"
                    lang="en"
                    value={coerceNumberInputString(form.energyCostPerUnit)}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="wa-input"
                  />
                </div>
                <div className="wa-form-field">
                  <label htmlFor="packagingCostPerUnit" className="wa-label">Packaging por unidad</label>
                  <input
                    id="packagingCostPerUnit"
                    name="packagingCostPerUnit"
                    type="number"
                    step="0.01"
                    lang="en"
                    value={coerceNumberInputString(form.packagingCostPerUnit)}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="wa-input"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {venueConfig.showDispenser && (
          <label className="flex items-start gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
            <input
              id="isDispensario"
              name="isDispensario"
              type="checkbox"
              checked={form.isDispensario}
              onChange={e => setForm(prev => ({ ...prev, isDispensario: e.target.checked }))}
              className="mt-0.5"
            />
            <span>{productLabel} de dispensario (solo en pestaña Dispensario)</span>
          </label>
        )}

        {isEdit && (
          <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Historial de costes</h3>
              <p className="text-xs text-gray-500">Puedes registrar cambios de coste sin tocar el resto del producto.</p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="wa-form-field">
                <label htmlFor="manual_supplier_name" className="wa-label">Proveedor</label>
                <input
                  id="manual_supplier_name"
                  name="supplier_name"
                  value={manualCost.supplier_name}
                  onChange={handleManualCostChange}
                  className="wa-input"
                />
              </div>
              <div className="wa-form-field">
                <label htmlFor="manual_effective_from" className="wa-label">Fecha efectiva</label>
                <input
                  id="manual_effective_from"
                  name="effective_from"
                  type="datetime-local"
                  value={manualCost.effective_from}
                  onChange={handleManualCostChange}
                  className="wa-input"
                />
              </div>
              <div className="wa-form-field">
                <label htmlFor="manual_cost_ex_vat" className="wa-label">Nuevo coste sin IVA</label>
                <input
                  id="manual_cost_ex_vat"
                  name="cost_ex_vat"
                  type="number"
                  step="0.01"
                  lang="en"
                  value={coerceNumberInputString(manualCost.cost_ex_vat)}
                  onChange={handleManualCostChange}
                  className="wa-input"
                />
              </div>
              <div className="wa-form-field">
                <label htmlFor="manual_purchase_vat_rate" className="wa-label">IVA compra (%)</label>
                <input
                  id="manual_purchase_vat_rate"
                  name="purchase_vat_rate"
                  type="number"
                  step="0.01"
                  lang="en"
                  value={coerceNumberInputString(manualCost.purchase_vat_rate)}
                  onChange={handleManualCostChange}
                  className="wa-input"
                />
              </div>
              <div className="wa-form-field">
                <label htmlFor="manual_cost_inc_vat" className="wa-label">Coste con IVA</label>
                <input
                  id="manual_cost_inc_vat"
                  name="cost_inc_vat"
                  type="number"
                  step="0.01"
                  lang="en"
                  value={coerceNumberInputString(manualCost.cost_inc_vat)}
                  onChange={handleManualCostChange}
                  className="wa-input"
                />
              </div>
              <div className="wa-form-field wa-form-field-full md:col-span-2">
                <label htmlFor="manual_notes" className="wa-label">Notas del cambio</label>
                <textarea
                  id="manual_notes"
                  name="notes"
                  value={manualCost.notes}
                  onChange={handleManualCostChange}
                  rows={2}
                  placeholder="Cambio de proveedor, subida de coste, nuevo lote..."
                  className="wa-input min-h-[84px]"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSaveManualCost}
                disabled={isSavingManualCost}
                className="wa-btn-secondary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSavingManualCost ? 'Guardando coste...' : 'Guardar nuevo coste'}
              </button>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-left text-sm text-gray-700">
                  <TableHead>
                    <Th className="px-3 py-2">Fecha</Th>
                    <Th className="px-3 py-2">Proveedor</Th>
                    <Th className="px-3 py-2">Coste s/IVA</Th>
                    <Th className="px-3 py-2">Coste c/IVA</Th>
                    <Th className="px-3 py-2">Notas</Th>
                  </TableHead>
                  <tbody>
                    {costHistoryLoading ? (
                      <tr>
                        <Td align="center" colSpan={5} className="px-3 py-4 text-[var(--muted-foreground)]">Cargando historial...</Td>
                      </tr>
                    ) : costHistory.length === 0 ? (
                      <tr>
                        <Td align="center" colSpan={5} className="px-3 py-4 text-[var(--muted-foreground)]">Todavía no hay histórico de costes.</Td>
                      </tr>
                    ) : (
                      costHistory.map((entry) => (
                        <Tr key={entry.id}>
                          <Td className="px-3 py-2">{formatHistoryDate(entry.effective_from || entry.created_at)}</Td>
                          <Td className="px-3 py-2">{entry.supplier_name || '-'}</Td>
                          <Td className="px-3 py-2">{formatCurrency(entry.cost_ex_vat)}</Td>
                          <Td className="px-3 py-2">{formatCurrency(entry.cost_inc_vat)}</Td>
                          <Td className="px-3 py-2">{entry.notes || '-'}</Td>
                        </Tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </form>
    </CustomModal>

    {/* Fuera del CustomModal, como en CustomerModal: los dos son portales y el que se
        monta después queda encima. */}
    <ConfirmDialog
      active={pendienteSinCategoria}
      title={`¿Crear el ${productEntityLabel} sin categoría?`}
      description={`Se usará la categoría por defecto. Puedes cambiarla luego editando el ${productEntityLabel}.`}
      confirmLabel="Crear igualmente"
      cancelLabel="Elegir categoría"
      onConfirm={() => {
        setPendienteSinCategoria(false);
        guardarProducto();
      }}
      onCancel={() => setPendienteSinCategoria(false)}
    />
    </>
  );
}

export default ProductModal;
